import { useSubscription } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import { useEnhancedMutation, useEnhancedQuery } from '@/modules/common/application';
import { useMessage } from '@/modules/common/application/context/message';
import {
  ConversationUpdatedSubscription,
  GetConversationsQuery,
  MarkConversationAsCompletedMutation,
  MarkConversationAsCompletedMutationVariables,
  MessageStatus,
  SenderType,
  StartConversationMutation,
  StartConversationMutationVariables,
} from '@/modules/common/types';

import { MARK_CONVERSATION_AS_COMPLETED, START_CONVERSATION } from '../apis/conversation.mutation';
import { CONVERSATION_UPDATED, GET_CONVERSATIONS } from '../apis/conversation.query';
import { ConversationStatus } from '../types/conversation';

export const useConversation = () => {
  const { setErrorMessage } = useMessage();
  const { t } = useTranslation();

  const { data, loading: conversationsLoading } = useEnhancedQuery<GetConversationsQuery>(
    GET_CONVERSATIONS,
    {
      onError: () => {
        setErrorMessage(t('messages.getConversations.error'));
      },
    }
  );

  useSubscription<ConversationUpdatedSubscription>(CONVERSATION_UPDATED, {
    onData: ({ client, data: subscription }) => {
      const updatedConversation = subscription.data?.conversationUpdated;

      if (!updatedConversation) {
        return;
      }

      const cacheId = client.cache.identify({
        __typename: 'Conversation',
        id: updatedConversation.id,
      });

      if (!cacheId) {
        return;
      }

      client.cache.modify({
        id: cacheId,
        fields: {
          title: () => updatedConversation.title ?? null,
        },
      });
    },
  });

  const [markConversationAsCompleted, { loading: markConversationAsCompletedLoading }] =
    useEnhancedMutation<
      MarkConversationAsCompletedMutation,
      MarkConversationAsCompletedMutationVariables
    >(MARK_CONVERSATION_AS_COMPLETED, {
      onError: () => {
        setErrorMessage(t('messages.markConversationAsCompleted.error'));
      },
      update: (cache, { data }) => {
        if (!data?.markConversationAsCompleted) return;
        cache.modify({
          id: cache.identify({
            __typename: 'Conversation',
            id: data.markConversationAsCompleted.id,
          }),
          fields: {
            isCompleted: () => data.markConversationAsCompleted.isCompleted,
          },
        });
      },
    });

  const [startConversation, { loading: startConversationLoading }] = useEnhancedMutation<
    StartConversationMutation,
    StartConversationMutationVariables
  >(START_CONVERSATION, {
    onError: () => {
      setErrorMessage(t('messages.startConversation.error'));
    },
    update: (cache, { data }) => {
      if (!data?.startConversation) return;
      const conversations = cache.readQuery<GetConversationsQuery>({
        query: GET_CONVERSATIONS,
      });
      if (!conversations?.conversations) return;
      cache.writeQuery({
        query: GET_CONVERSATIONS,
        data: {
          conversations: [...conversations.conversations, data.startConversation],
        },
      });
    },
  });

  const calculateConversationStatus = (
    isCompleted: boolean,
    lastMessage: NonNullable<GetConversationsQuery['conversations']>[number]['lastMessage']
  ) => {
    if (isCompleted) {
      return ConversationStatus.COMPLETED;
    }
    if (lastMessage?.status === MessageStatus.IN_PROGRESS) {
      return ConversationStatus.PROCESSING;
    }
    if (lastMessage?.sender === SenderType.BOT) {
      return ConversationStatus.WAITING_FOR_INPUT;
    }
    return ConversationStatus.PROCESSING;
  };

  const sortedConversations = data?.conversations
    ? [...data.conversations]
        .sort((a, b) => {
          if (!a.lastMessage || !b.lastMessage) return 0;
          return (
            new Date(b.lastMessage.createdAt).getTime() -
            new Date(a.lastMessage.createdAt).getTime()
          );
        })
        .map((conversation) => ({
          ...conversation,
          status: calculateConversationStatus(conversation.isCompleted, conversation.lastMessage),
        }))
    : [];

  function getConversationById(id: string) {
    return sortedConversations.find((conversation) => conversation.id === id);
  }

  return {
    conversations: sortedConversations,
    conversationsLoading,
    markConversationAsCompleted,
    markConversationAsCompletedLoading,
    getConversationById,
    startConversation,
    startConversationLoading,
  };
};
